import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';

export default function ServicesTab({ user, centerData, onRefresh }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm] = useState({ name: '', description: '', price: '', durationMinutes: '', isActive: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadServices(); }, [user.uid]);

  const loadServices = async () => {
    try {
      const snap = await getDocs(collection(db, 'serviceCenters', user.uid, 'services'));
      setServices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openAdd = () => { setEditing(null); setForm({ name: '', description: '', price: '', durationMinutes: '', isActive: true }); setShowForm(true); };
  const openEdit = (svc) => { setEditing(svc); setForm({ name: svc.name, description: svc.description || '', price: String(svc.price), durationMinutes: String(svc.durationMinutes || ''), isActive: svc.isActive !== false }); setShowForm(true); };

  const save = async () => {
    if (!form.name || !form.price) { alert('Name and price are required.'); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), description: form.description.trim(), price: Number(form.price), durationMinutes: form.durationMinutes ? Number(form.durationMinutes) : null, isActive: form.isActive, updatedAt: serverTimestamp() };
      if (editing) {
        await updateDoc(doc(db, 'serviceCenters', user.uid, 'services', editing.id), payload);
      } else {
        await addDoc(collection(db, 'serviceCenters', user.uid, 'services'), { ...payload, createdAt: serverTimestamp() });
      }
      setShowForm(false);
      loadServices();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    await deleteDoc(doc(db, 'serviceCenters', user.uid, 'services', id));
    loadServices();
  };

  const toggle = async (svc) => {
    await updateDoc(doc(db, 'serviceCenters', user.uid, 'services', svc.id), { isActive: !svc.isActive });
    loadServices();
  };

  const iS = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, color: '#111827', background: '#f9fafb', outline: 'none', boxSizing: 'border-box' };
  const lS = { display: 'block', fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 };

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 900 }}>Services & Pricing</h1>
          <p style={{ color: '#6b7280', marginTop: 4 }}>Manage the services you offer through CocoWash</p>
        </div>
        <button onClick={openAdd} style={{ background: '#F97316', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>+ Add Service</button>
      </div>

      {loading ? <p style={{ color: '#6b7280' }}>Loading...</p> : services.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 16, padding: 48, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔧</div>
          <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>No services yet</h3>
          <p style={{ color: '#6b7280', marginBottom: 20 }}>Add your first service to start receiving CocoWash customers</p>
          <button onClick={openAdd} style={{ background: '#F97316', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 24px', fontWeight: 700, cursor: 'pointer' }}>Add Your First Service</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {services.map(svc => (
            <div key={svc.id} style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderTop: `3px solid ${svc.isActive ? '#F97316' : '#e5e7eb'}`, opacity: svc.isActive ? 1 : 0.6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <h3 style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{svc.name}</h3>
                <span style={{ fontSize: 18, fontWeight: 900, color: '#F97316' }}>${svc.price}</span>
              </div>
              {svc.description && <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5, marginBottom: 10 }}>{svc.description}</p>}
              {svc.durationMinutes && <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10 }}>⏱ {svc.durationMinutes} minutes</p>}
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={() => toggle(svc)} style={{ flex: 1, padding: '8px', background: svc.isActive ? '#f0fdf4' : '#f3f4f6', border: `1px solid ${svc.isActive ? '#bbf7d0' : '#e5e7eb'}`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', color: svc.isActive ? '#16a34a' : '#6b7280' }}>{svc.isActive ? 'Active' : 'Inactive'}</button>
                <button onClick={() => openEdit(svc)} style={{ flex: 1, padding: '8px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#3b82f6' }}>Edit</button>
                <button onClick={() => remove(svc.id)} style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#dc2626' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: '100%', maxWidth: 480 }}>
            <h2 style={{ fontSize: 20, fontWeight: 900, marginBottom: 24 }}>{editing ? 'Edit Service' : 'Add New Service'}</h2>
            <label style={lS}>Service Name *</label>
            <input style={{ ...iS, marginBottom: 14 }} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Full Detail, Oil Change" />
            <label style={lS}>Description</label>
            <textarea style={{ ...iS, minHeight: 70, resize: 'vertical', marginBottom: 14 }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What is included in this service?" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div><label style={lS}>Price ($) *</label><input style={iS} type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="0.00" /></div>
              <div><label style={lS}>Duration (minutes)</label><input style={iS} type="number" value={form.durationMinutes} onChange={e => setForm(f => ({ ...f, durationMinutes: e.target.value }))} placeholder="60" /></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
              <input type="checkbox" id="active" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} style={{ accentColor: '#F97316' }} />
              <label htmlFor="active" style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Active — visible to CocoWash customers</label>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setShowForm(false)} style={{ flex: 1, padding: '12px', background: '#f3f4f6', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={save} disabled={saving} style={{ flex: 1, padding: '12px', background: '#F97316', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save Service'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}